import { uploadKbAttachments } from "controller/api/kb";

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export const uploadImage = async (file: File): Promise<UploadResult> => {
  return new Promise((resolve) => {
    const formData = new FormData();
    formData.append("file", file);

    const uploadSubscription = uploadKbAttachments({ formData });

    uploadSubscription.subscribe({
      next: (result) => {
        if (result.status === "COMPLETE") {
          if (
            result.response?.success ||
            result.response?.status === "success"
          ) {
            // Handle both response formats
            const url =
              result.response.data?.url || result.response.data?.Location;
            resolve({
              success: true,
              url: url,
            });
          } else {
            resolve({
              success: false,
              error: "Upload failed - invalid response",
            });
          }
        }
      },
      error: (error) => {
        resolve({
          success: false,
          error: error.message || "Upload failed",
        });
      },
    });
  });
};

export const createBase64Preview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result as string);
    };
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  });
};

export const createLoadingPlaceholder = ({
  width = 200,
  height = 100,
}: {
  width?: number;
  height?: number;
}): string => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'%3E%3Crect width='${width}' height='${height}' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999'%3EUploading...%3C/text%3E%3C/svg%3E`;
};

export const createErrorPlaceholder = (): string => {
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100'%3E%3Crect width='200' height='100' fill='%23ffebee'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23c62828'%3EUpload failed%3C/text%3E%3C/svg%3E";
};

export const generatePlaceholderId = (): string => {
  return `placeholder-${Date.now()}-${Math.random()}`;
};

export const setPlaceholderError = (
  editor: any,
  placeholderId: string,
  errorMessage: string,
) => {
  editor
    .chain()
    .focus()
    .command(({ tr, state }: any) => {
      const doc = state.doc;
      let placeholderPos: number | null = null;
      let placeholderNode: any = null;

      doc.descendants((node: any, pos: number) => {
        if (
          (node.type.name === "resizableImage" ||
            node.type.name === "fileChip") &&
          node.attrs["data-placeholder-id"] === placeholderId
        ) {
          placeholderPos = pos;
          placeholderNode = node;
          return false;
        }
      });

      if (placeholderPos !== null && placeholderNode) {
        tr.setNodeMarkup(placeholderPos, null, {
          ...placeholderNode.attrs,
          isLoading: false,
          errorMessage,
          "data-placeholder-id": null,
        });
        return true;
      }
      return false;
    })
    .run();
};

export const findAndReplaceImagePlaceholder = (
  editor: any,
  placeholderId: string,
  newSrc: string,
) => {
  editor
    .chain()
    .focus()
    .command(({ tr, state }: any) => {
      const doc = state.doc;
      let placeholderPos: number | null = null;
      let placeholderNode: any = null;

      doc.descendants((node: any, pos: number) => {
        if (
          (node.type.name === "image" ||
            node.type.name === "resizableImage" ||
            node.type.name === "imagePlaceholder" ||
            node.type.name === "fileChip" ||
            node.type.name === "pdfEmbed") &&
          node.attrs["data-placeholder-id"] === placeholderId
        ) {
          placeholderPos = pos;
          placeholderNode = node;
          return false;
        }
      });

      if (placeholderPos !== null && placeholderNode) {
        tr.setNodeMarkup(placeholderPos, null, {
          ...placeholderNode.attrs,
          src: newSrc,
          isLoading: false,
          "data-placeholder-id": null,
        });
        return true;
      } else {
        return false;
      }
    })
    .run();
};
